import { HttpClient } from "@angular/common/http";
import { LoginService } from "./../../../login/login.service";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { LocalStorageService } from "src/app/utils/localstorage.service";
import { environment } from "../../../../environments/environment";
import { Observable, Subject } from "rxjs";

@Injectable({ providedIn: "root" })
export class AccountantService {
  current_company = new Subject<any>();
  update_company_name_sub = new Subject<any>();
  clients = new Subject<any>();
  archiveCompany = new Subject<any>();
  headers = new Headers();

  constructor(
    private http: HttpClient,
    private localStorage: LocalStorageService,
    private router: Router,
    private loginService: LoginService,
  ) {}

  get_clients(): Promise<any> {
    return this.http
      .get(`${environment.apiUrl}/user/accountant.client.list`)
      .toPromise();
  }

  get_clients_simple(): Promise<any> {
    return this.http
      .get(`${environment.apiUrl}/user/accountant.client.list.simple`)
      .toPromise();
  }

  get_clients_paginated(page: number): Promise<any> {
    return this.http
      .get(
        `${environment.apiUrl}/user/accountant.client.list.paginated?page=${page}`,
      )
      .toPromise();
  }

  getWorkspacesPaginated(currLength = 0) {
    let url = `${environment.apiUrl}/user/workspaces`;
    if (currLength != 0) {
      url = `${url}?currLength=${currLength}`;
    }
    return this.http.get(url).toPromise();
  }

  get_client_info(company_id): Promise<any> {
    return this.http
      .get(`${environment.apiUrl}/company/${company_id}`)
      .toPromise();
  }

  add_client_app(user_id, app_id): Promise<any> {
    return this.http
      .post(`${environment.apiUrl}/user/${user_id}/apps`, { app_id: app_id })
      .toPromise();
  }

  delete_client_app(user_id, app_id): Promise<any> {
    return this.http
      .patch(`${environment.apiUrl}/user/${user_id}/apps`, { app_id: app_id })
      .toPromise();
  }

  update_client_app_order(user_id, apps): Promise<any> {
    return this.http
      .put(`${environment.apiUrl}/user/${user_id}/apps`, { apps: apps })
      .toPromise();
  }

  add_company_app(company_id, app_id): Promise<any> {
    return this.http
      .post(`${environment.apiUrl}/company/${company_id}/apps`, {
        app_id: app_id,
      })
      .toPromise();
  }

  delete_company_app(company_id, app_id): Promise<any> {
    return this.http
      .patch(`${environment.apiUrl}/company/${company_id}/apps`, {
        app_id: app_id,
      })
      .toPromise();
  }

  update_company_app_order(company_id, apps): Promise<any> {
    return this.http
      .put(`${environment.apiUrl}/company/${company_id}/apps`, { apps: apps })
      .toPromise();
  }

  add_company_resource(company_id, resource_id): Promise<any> {
    return this.http
      .post(`${environment.apiUrl}/company/${company_id}/resources`, {
        resource_id: resource_id,
      })
      .toPromise();
  }

  delete_company_resource(company_id, resource_id): Promise<any> {
    return this.http
      .patch(`${environment.apiUrl}/company/${company_id}/resources`, {
        resource_id: resource_id,
      })
      .toPromise();
  }

  update_company_resource_order(company_id, resources): Promise<any> {
    return this.http
      .put(`${environment.apiUrl}/company/${company_id}/resources`, {
        resources: resources,
      })
      .toPromise();
  }

  get_company_info(): Promise<any> {
    return this.http.get(`${environment.apiUrl}/company/`).toPromise();
  }

  update_company_info(company): Promise<any> {
    return this.http
      .patch(`${environment.apiUrl}/company/`, company)
      .toPromise();
  }

  update_client_notes(company_id, notes): Promise<any> {
    return this.http
      .patch(`${environment.apiUrl}/company/${company_id}/notes`, { notes })
      .toPromise();
  }

  get_client_notes(company_id): Observable<any> {
    return this.http.get(`${environment.apiUrl}/company/${company_id}/notes`);
  }

  invite_client(
    name,
    email,
    company_name,
    type,
    invited_to,
    access_level,
  ): Promise<any> {
    const data = {
      name: name,
      email: email,
      company_name: company_name,
      type: type,
      invited_to: invited_to,
      access_level: access_level,
    };
    return this.http
      .post(`${environment.apiUrl}/user/invite`, data)
      .toPromise();
  }

  add_existing_clients(clients, company_id): Promise<any> {
    const data = {
      user_ids: clients,
      invited_to: company_id,
    };
    return this.http
      .post(`${environment.apiUrl}/user/invite.many`, data)
      .toPromise();
  }

  add_resource(resource): Promise<any> {
    return this.http
      .post(`${environment.apiUrl}/resources/`, resource)
      .toPromise();
  }

  edit_resource(resource, resource_id): Promise<any> {
    return this.http
      .patch(`${environment.apiUrl}/resources/${resource_id}`, resource)
      .toPromise();
  }

  delete_resource(resource_id): Promise<any> {
    return this.http
      .delete(`${environment.apiUrl}/resources/${resource_id}`)
      .toPromise();
  }

  add_app(app): Promise<any> {
    return this.http.post(`${environment.apiUrl}/apps/`, app).toPromise();
  }

  edit_app(app, app_id): Promise<any> {
    return this.http
      .patch(`${environment.apiUrl}/apps/${app_id}`, app)
      .toPromise();
  }

  delete_app(app_id): Promise<any> {
    return this.http.delete(`${environment.apiUrl}/apps/${app_id}`).toPromise();
  }

  send_invite_email(invite_id): Promise<any> {
    return this.http
      .post(`${environment.apiUrl}/user/invite/email/${invite_id}`, {})
      .toPromise();
  }

  remove_invite(invite_id): Promise<any> {
    return this.http
      .delete(`${environment.apiUrl}/user/invite/remove/${invite_id}`)
      .toPromise();
  }

  get_practice_apps(): Promise<any> {
    return this.http.get(`${environment.apiUrl}/apps/manage`).toPromise();
  }

  save_accountant_assignments(
    accountant_id,
    clients,
    loaded_clients = [],
  ): Promise<any> {
    const data = {
      accountant_id: accountant_id,
      clients: clients,
      loaded_clients: loaded_clients,
    };

    return this.http
      .post(`${environment.apiUrl}/user/accountant.client.add`, data)
      .toPromise();
  }

  assignCompanyAccountants(
    company: number,
    accountants: number[],
  ): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/user/accountant.client.add`, {
      company,
      accountants,
    });
  }

  change_access_level(accountant_id, access_level): Promise<any> {
    const data = {
      accountant_id: accountant_id,
      access_level: access_level,
    };

    return this.http
      .post(`${environment.apiUrl}/user/accountant.update.access_level`, data)
      .toPromise();
  }

  update_company_name(company_id, name): Promise<any> {
    return this.http
      .patch(`${environment.apiUrl}/company/${company_id}`, { name: name })
      .toPromise();
  }

  archive_company(company_id): Promise<any> {
    return this.http
      .patch(`${environment.apiUrl}/company/${company_id}/archive`, {})
      .toPromise();
  }

  add_payment_method(stripe_obj): Promise<any> {
    return this.http
      .post(`${environment.apiUrl}/subscriptions/add-token`, stripe_obj)
      .toPromise();
  }

  remove_payment_method(): Promise<any> {
    return this.http
      .delete(`${environment.apiUrl}/subscriptions/add-token`)
      .toPromise();
  }

  get_subscription_status(): Promise<any> {
    return this.http.get(`${environment.apiUrl}/subscriptions/`).toPromise();
  }

  getSubscriberInfo(): Promise<any> {
    return this.http
      .get(`${environment.apiUrl}/subscriptions/subscriber-info`)
      .toPromise();
  }

  delete_staff(user_id): Promise<any> {
    return this.http
      .delete(`${environment.apiUrl}/user/${user_id}/management`)
      .toPromise();
  }

  delete_staff_with_status(user_id): Promise<any> {
    return this.http
      .delete(`${environment.apiUrl}/user/${user_id}/management`, { observe: 'response' })
      .toPromise();
  }

  get_staff_assignments(user_id): Promise<any> {
    return this.http
      .get(`${environment.apiUrl}/user/${user_id}/check-assignments`)
      .toPromise();
  }

  delete_client_user(user_id, new_owner, company_id = null): Promise<any> {
    return this.http
      .post(`${environment.apiUrl}/user/${user_id}/client.management`, {
        new_owner: new_owner,
        company_id: company_id,
      })
      .toPromise();
  }

  apply_coupon(coupon): Promise<any> {
    return this.http
      .post(`${environment.apiUrl}/subscriptions/coupon`, { coupon: coupon })
      .toPromise();
  }

  get_accoutant_client_users(company_id): Promise<any> {
    return this.http
      .get(`${environment.apiUrl}/user/accountant.client.users/${company_id}`)
      .toPromise();
  }

  getStaffMembers(companyId: number) {
    return this.http.get(
      `${environment.apiUrl}/conversations/company/${companyId}/staff`,
    );
  }

  get_client_details(company_id): Promise<any> {
    return this.http
      .get(`${environment.apiUrl}/company/${company_id}/details`)
      .toPromise();
  }

  getClientsOnly(company_id: number): Promise<any> {
    return this.http
      .get(`${environment.apiUrl}/company/${company_id}/clients`)
      .toPromise();
  }

  unassignAccountantFromClient(accountant_id, company_id): Promise<any> {
    return this.http
      .delete(
        `${environment.apiUrl}/user/${accountant_id}/company/${company_id}/unassign`,
      )
      .toPromise();
  }

  addTag(tag: Object): Promise<any> {
    return this.http.post(`${environment.apiUrl}/tags`, tag).toPromise();
  }

  removeTag(tag_uuid): Promise<any> {
    return this.http
      .delete(`${environment.apiUrl}/tags/${tag_uuid}`)
      .toPromise();
  }

  getAccountingCompanyTags(): Promise<any> {
    return this.http.get(`${environment.apiUrl}/tags`).toPromise();
  }

  addWorkspaceTag(workspace_uuid, tag_uuid): Promise<any> {
    return this.http
      .post(
        `${environment.apiUrl}/tags/${tag_uuid}/workspace.add/${workspace_uuid}`,
        {},
      )
      .toPromise();
  }

  getWorkspaceTags(workspace_uuid): Promise<any> {
    return this.http
      .get(`${environment.apiUrl}/tags/workspace/${workspace_uuid}`)
      .toPromise();
  }

  removeWorkspaceTag(workspace_uuid, tag_uuid): Promise<any> {
    return this.http
      .delete(
        `${environment.apiUrl}/tags/${tag_uuid}/workspace.remove/${workspace_uuid}`,
      )
      .toPromise();
  }

  getTagsByWorkspace(): Promise<any> {
    return this.http.get(`${environment.apiUrl}/tags/workspaces`).toPromise();
  }

  addJobTag(job_uuid, tag_uuid): Promise<any> {
    return this.http
      .post(`${environment.apiUrl}/tags/${tag_uuid}/job.add/${job_uuid}`, {})
      .toPromise();
  }

  removeJobTag(job_uuid, tag_uuid): Promise<any> {
    return this.http
      .delete(`${environment.apiUrl}/tags/${tag_uuid}/job.remove/${job_uuid}`)
      .toPromise();
  }

  editTag(tag): Promise<any> {
    return this.http
      .patch(`${environment.apiUrl}/tags/${tag.uuid}`, tag)
      .toPromise();
  }

  getDefaultInviteEmail() {
    const url = `${environment.apiUrl}/company/default-email`;
    return this.http.get(url).toPromise();
  }

  changeDefaultInviteEmail(changedDefaultEmail: string) {
    const url = `${environment.apiUrl}/company/default-email`;
    const data = { default_email: changedDefaultEmail };
    return this.http.put(url, data).toPromise();
  }

  add_company_default_app(appId: number) {
    const url = `${environment.apiUrl}/apps/accounting-company/default-apps/`;
    return this.http.post(url, { app_id: appId }).toPromise();
  }

  get_company_default_apps() {
    const url = `${environment.apiUrl}/apps/accounting-company/default-apps/`;
    return this.http.get(url).toPromise();
  }

  delete_company_default_app(appId: number) {
    const params = `?app_id=${appId}`;
    const url = `${environment.apiUrl}/apps/accounting-company/default-apps/${params}`;
    return this.http.delete(url).toPromise();
  }

  add_company_default_resource(resourceId: number) {
    const url = `${environment.apiUrl}/resources/accounting-company/default-resources/`;
    return this.http.post(url, { resource_id: resourceId }).toPromise();
  }

  get_company_default_resources() {
    const url = `${environment.apiUrl}/resources/accounting-company/default-resources/`;
    return this.http.get(url).toPromise();
  }

  delete_company_default_resource(resourceId: number) {
    const params = `?resource_id=${resourceId}`;
    const url = `${environment.apiUrl}/resources/accounting-company/default-resources/${params}`;
    return this.http.delete(url).toPromise();
  }

  get_company_emails(companyUuid: string): Promise<any> {
    const url = `${environment.apiUrl}/company/${companyUuid}/emails`;
    return this.http.get(url).toPromise();
  }
}
